import { useEffect, useMemo, useState } from 'react';
import { Coins, Minus, Plus, RotateCcw } from 'lucide-react';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { rupiah, number as fmtNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Indonesian Rupiah denominations, largest first (notes then coins). */
const DENOMS = [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100] as const;

/** One denomination row: label · [− qty +] · subtotal. */
function DenomRow({
  denom,
  qty,
  onChange,
}: {
  denom: number;
  qty: number;
  onChange: (next: number) => void;
}) {
  const set = (n: number) => onChange(Math.max(0, Math.min(9999, n)));
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <span className="tnum w-20 shrink-0 font-sans text-sm font-medium text-ink-900">{rupiah(denom)}</span>
      <div className="flex items-center overflow-hidden rounded-lg border border-cream-200">
        <button
          type="button"
          onClick={() => set(qty - 1)}
          disabled={qty === 0}
          aria-label={`Kurangi ${rupiah(denom)}`}
          className="flex h-8 w-8 items-center justify-center text-ink-600 transition-colors hover:bg-cream-100 disabled:cursor-not-allowed disabled:text-ink-400/50"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          value={qty === 0 ? '' : qty}
          placeholder="0"
          onChange={(e) => set(Math.floor(Number(e.target.value) || 0))}
          aria-label={`Jumlah lembar/keping ${rupiah(denom)}`}
          className="tnum h-8 w-12 border-x border-cream-200 bg-white text-center text-sm text-ink-900 outline-none focus:bg-cream-100/50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        />
        <button
          type="button"
          onClick={() => set(qty + 1)}
          aria-label={`Tambah ${rupiah(denom)}`}
          className="flex h-8 w-8 items-center justify-center text-ink-600 transition-colors hover:bg-cream-100"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
      <span className={cn('tnum flex-1 text-right text-sm', qty > 0 ? 'text-ink-900' : 'text-ink-400')}>
        {qty > 0 ? rupiah(denom * qty) : '—'}
      </span>
    </div>
  );
}

/**
 * Cash-drawer count / reconciliation. The cashier taps the count per
 * denomination; the panel sums the till and compares it to the expected cash,
 * surfacing over/short. Purely a count aid — nothing is persisted (no shift is
 * closed), so refreshing clears it.
 */
export function CashCountPanel({
  expectedCash,
  onChange,
}: {
  expectedCash: number;
  /** Emits the running count so a parent can close the shift with it. */
  onChange?: (counted: number, denominations: Record<string, number>) => void;
}) {
  const [counts, setCounts] = useState<Record<number, number>>({});

  const counted = useMemo(
    () => DENOMS.reduce((sum, d) => sum + d * (counts[d] ?? 0), 0),
    [counts],
  );
  const pieces = useMemo(() => DENOMS.reduce((sum, d) => sum + (counts[d] ?? 0), 0), [counts]);
  const variance = counted - expectedCash;
  const started = pieces > 0;

  useEffect(() => {
    if (!onChange) return;
    const denoms: Record<string, number> = {};
    for (const d of DENOMS) {
      const c = counts[d] ?? 0;
      if (c > 0) denoms[String(d)] = c;
    }
    onChange(counted, denoms);
  }, [counts, counted, onChange]);

  const set = (denom: number, qty: number) => setCounts((c) => ({ ...c, [denom]: qty }));
  const reset = () => setCounts({});

  // Half the denominations per column so the grid stays short on a POS screen.
  const half = Math.ceil(DENOMS.length / 2);

  return (
    <section className="overflow-hidden rounded-2xl border border-cream-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-cream-200 px-5 py-3">
        <h2 className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-ink-600">
          <Coins className="h-4 w-4 text-gold-600" aria-hidden="true" /> Hitung laci kas
        </h2>
        <button
          type="button"
          onClick={reset}
          disabled={!started}
          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-ink-600 transition-colors hover:bg-cream-100 disabled:cursor-not-allowed disabled:text-ink-400/60"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Reset
        </button>
      </div>

      <div className="grid gap-x-6 px-5 py-3 sm:grid-cols-2">
        <div className="divide-y divide-cream-200/70">
          {DENOMS.slice(0, half).map((d) => (
            <DenomRow key={d} denom={d} qty={counts[d] ?? 0} onChange={(n) => set(d, n)} />
          ))}
        </div>
        <div className="divide-y divide-cream-200/70">
          {DENOMS.slice(half).map((d) => (
            <DenomRow key={d} denom={d} qty={counts[d] ?? 0} onChange={(n) => set(d, n)} />
          ))}
        </div>
      </div>

      {/* Reconciliation footer */}
      <div className="border-t border-cream-200 bg-cream-100/50 px-5 py-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">
              Dihitung {started && <span className="tnum font-normal normal-case">· {fmtNumber(pieces)} keping</span>}
            </p>
            <MoneyValue value={counted} className="text-2xl" />
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Diharapkan</p>
            <MoneyValue value={expectedCash} className="text-lg" />
          </div>
        </div>

        <div
          className={cn(
            'mt-3 flex items-center justify-between rounded-xl px-4 py-2.5',
            !started
              ? 'bg-white text-ink-400'
              : variance === 0
                ? 'bg-emerald-50 text-emerald-700'
                : variance > 0
                  ? 'bg-sky-50 text-sky-700'
                  : 'bg-red-50 text-crimson-700',
          )}
        >
          <span className="text-sm font-semibold">
            {!started ? 'Selisih' : variance === 0 ? 'Sesuai' : variance > 0 ? 'Lebih' : 'Kurang'}
          </span>
          <span className="tnum font-display text-xl font-bold">
            {!started ? '—' : variance === 0 ? rupiah(0) : `${variance > 0 ? '+' : '−'} ${rupiah(Math.abs(variance))}`}
          </span>
        </div>
      </div>
    </section>
  );
}
